// src/components/workloads/creation/SaturationPointSelector.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { Pentagon, Loader2, AlertTriangle, Check, Target, Map, X, MapPin, Download, Route } from 'lucide-react';
import { Marker, Polygon, Polyline } from '@react-google-maps/api';
import { useWorkloadStore, PolygonTerritory } from '../../../store/workloadStore';
import { useProject } from '../../../contexts/ProjectContext';
import GoogleMapProvider from '../../map/GoogleMapProvider';
import BaseMap from '../../map/BaseMap';
import PlacesAutocomplete from '../../map/PlacesAutocomplete';
import { addressDatabaseService, AddressData } from '../../../services/addressDatabase.service';
import { walkingPatternService } from '../../../services/walkingPattern.service';
import { exportCreationAddressesToCSV } from '../../../utils/addressExport';

// Define our own LatLng type to avoid dependency on google.maps before load
interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface DrawnPolygon {
  id: string;
  paths: LatLngLiteral[];
  bounds: google.maps.LatLngBoundsLiteral;
}

const POLYGON_COLORS = ['#4F46E5', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#BE185D', '#65A30D'];
const colorAt = (i: number) => POLYGON_COLORS[i % POLYGON_COLORS.length];
const boundsOf = (paths: LatLngLiteral[]): google.maps.LatLngBoundsLiteral => {
  const b = new google.maps.LatLngBounds();
  paths.forEach((p) => b.extend(p));
  return b.toJSON();
};

// Point targeting modal props
type SearchMode = 'closest' | 'walking' | 'communities';

interface PointTargetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (targetCount: number, mode: SearchMode, maxTotalHomes?: number) => void;
  position: LatLngLiteral | null;
}

const PointTargetModal: React.FC<PointTargetModalProps> = ({ isOpen, onClose, onSubmit, position }) => {
  const [targetCount, setTargetCount] = useState(500);
  const [targetCountInput, setTargetCountInput] = useState('500');
  const [mode, setMode] = useState<SearchMode>('walking');
  const [maxTotalHomes, setMaxTotalHomes] = useState(2500);
  const [maxTotalHomesInput, setMaxTotalHomesInput] = useState('2500');

  // Handle target count input change
  const handleTargetCountChange = (value: string) => {
    setTargetCountInput(value);
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 1) {
      setTargetCount(Math.min(5000, num));
    }
  };

  // Handle max total homes input change
  const handleMaxTotalHomesChange = (value: string) => {
    setMaxTotalHomesInput(value);
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 1) {
      setMaxTotalHomes(Math.min(10000, num));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Target className="h-5 w-5 mr-2 text-indigo-600" />
            Point-Based Targeting
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {position && (
            <p className="text-sm text-gray-500">
              Center point: {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
            </p>
          )}

          {/* Search mode picker */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Search mode</label>

            <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer ${mode === 'walking' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio"
                name="search-mode"
                checked={mode === 'walking'}
                onChange={() => setMode('walking')}
                className="mt-0.5 h-4 w-4 text-indigo-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <Route className="h-4 w-4 text-indigo-600" />
                  Walking pattern (snap to streets)
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  Follows real OSM road geometry — addresses come out in a sensible street-by-street order, the way a canvasser actually walks.
                </p>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer ${mode === 'closest' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio"
                name="search-mode"
                checked={mode === 'closest'}
                onChange={() => setMode('closest')}
                className="mt-0.5 h-4 w-4 text-indigo-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <Target className="h-4 w-4 text-gray-600" />
                  Closest first (concentric)
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  Original behavior: expands outward from the center point. Faster on sparse rural data.
                </p>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer ${mode === 'communities' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio"
                name="search-mode"
                checked={mode === 'communities'}
                onChange={() => setMode('communities')}
                className="mt-0.5 h-4 w-4 text-indigo-600"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <Map className="h-4 w-4 text-gray-600" />
                  Map all proximal communities
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  Auto-discover and split nearby neighborhoods/complexes into separate workloads (~500 homes each).
                </p>
              </div>
            </label>
          </div>

          {mode !== 'communities' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How many homes do you want to target?
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={targetCountInput}
                onChange={(e) => handleTargetCountChange(e.target.value)}
                onBlur={() => setTargetCountInput(String(targetCount))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter number of homes"
              />
            </div>
          )}

          {mode === 'communities' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum total homes to map
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={maxTotalHomesInput}
                onChange={(e) => handleMaxTotalHomesChange(e.target.value)}
                onBlur={() => setMaxTotalHomesInput(String(maxTotalHomes))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter maximum homes"
              />
              <p className="text-xs text-gray-500 mt-1">
                Limits the total addresses discovered. Will create {Math.ceil(maxTotalHomes / 500)} workloads of ~500 homes each.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(targetCount, mode, mode === 'communities' ? maxTotalHomes : undefined)}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
          >
            {mode === 'communities'
              ? `Map Up To ${maxTotalHomes} Homes`
              : mode === 'walking'
              ? `Walk to ${targetCount} Homes`
              : `Find ${targetCount} Homes`}
          </button>
        </div>
      </div>
    </div>
  );
};

const SaturationPointSelector: React.FC = () => {
  const {
    creation,
    setCreationTerritory,
    setCreationAddresses,
    addCreationPolygon,
    clearCreationPolygons,
  } = useWorkloadStore();

  const { selectedProject } = useProject();

  // Default center (US center), will be overridden by school address if available
  const defaultCenter: LatLngLiteral = {
    lat: 39.8283,
    lng: -98.5795,
  };

  // Get school address location if available
  const schoolLocation = selectedProject?.schoolAddress?.location;

  // Mode: 'marker' = point targeting, 'polygon' = draw areas, null = idle
  const [drawingMode, setDrawingMode] = useState<'polygon' | 'marker' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<LatLngLiteral>(
    schoolLocation ? { lat: schoolLocation.lat, lng: schoolLocation.lng } : defaultCenter
  );
  const [searchMarker, setSearchMarker] = useState<LatLngLiteral | null>(
    schoolLocation ? { lat: schoolLocation.lat, lng: schoolLocation.lng } : null
  );
  const [apartmentStats, setApartmentStats] = useState<{ buildings: number; units: number } | null>(null);

  // Click-to-place polygon drawing state
  const [vertices, setVertices] = useState<LatLngLiteral[]>([]);
  const [polygons, setPolygons] = useState<DrawnPolygon[]>([]);
  const [hasSetInitialCenter, setHasSetInitialCenter] = useState(false);

  // Point targeting state
  const [showPointModal, setShowPointModal] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<LatLngLiteral | null>(null);
  const [communityWorkloads, setCommunityWorkloads] = useState<Array<{ name: string; count: number }>>([]);

  // Extract street name from full address for grouping.
  const extractStreetName = (fullStreet: string): string =>
    fullStreet.replace(/^\d+\s*/, '').trim();

  // Update map center when project changes (and has school address)
  useEffect(() => {
    if (schoolLocation && !hasSetInitialCenter) {
      setMapCenter({ lat: schoolLocation.lat, lng: schoolLocation.lng });
      setSearchMarker({ lat: schoolLocation.lat, lng: schoolLocation.lng });
      setCreationTerritory({ center: { lat: schoolLocation.lat, lng: schoolLocation.lng } });
      setHasSetInitialCenter(true);
    }
  }, [schoolLocation, hasSetInitialCenter, setCreationTerritory]);

  // Handle place selection from search
  const handlePlaceSelect = useCallback((place: { location: { lat: number; lng: number } }) => {
    setMapCenter(place.location);
    setSearchMarker(place.location);
    setCreationTerritory({ center: place.location });
  }, [setCreationTerritory]);

  // Handle marker drag
  const handleMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setSearchMarker(newPos);
      setMapCenter(newPos);
      setCreationTerritory({ center: newPos });
    }
  }, [setCreationTerritory]);

  // Handle map click: point mode opens the targeting modal; polygon mode adds a vertex.
  const handleMapClick = useCallback((pos: LatLngLiteral) => {
    if (drawingMode === 'marker') {
      setSelectedPoint(pos);
      setSearchMarker(pos);
      setMapCenter(pos);
      setCreationTerritory({ center: pos });
      setShowPointModal(true);
      setDrawingMode(null);
    } else if (drawingMode === 'polygon') {
      setVertices((v) => [...v, pos]);
    }
  }, [drawingMode, setCreationTerritory]);

  const finishArea = useCallback(() => {
    if (vertices.length < 3) return;
    const poly: DrawnPolygon = {
      id: `poly_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      paths: vertices,
      bounds: boundsOf(vertices),
    };
    setPolygons((prev) => [...prev, poly]);
    setVertices([]);
    setError(null);
  }, [vertices]);

  // Handle point targeting submission
  const handlePointTargetSubmit = useCallback(async (targetCount: number, mode: SearchMode, maxTotalHomes?: number) => {
    if (!selectedPoint) return;

    setShowPointModal(false);
    setIsLoading(true);
    setError(null);
    setProgressMessage(null);
    setApartmentStats(null);
    clearCreationPolygons();
    setCommunityWorkloads([]);

    try {
      let foundAddresses = 0; // Track locally to avoid race condition

      if (mode === 'walking') {
        // OSM-snapped walking pattern. Falls back to closest-first if street
        // data is too sparse.
        try {
          setProgressMessage('Building walking route from street network…');
          const result = await walkingPatternService.getAddressesAlongWalkingRoute(
            selectedPoint,
            targetCount,
            (msg) => setProgressMessage(msg)
          );

          const addresses = result.addresses;
          foundAddresses = addresses.length;

          const workloadAddresses = addresses.map((addr: AddressData) => {
            const fullStreet = `${addr.houseNumber} ${addr.street}`.trim();
            return {
              street: fullStreet,
              unit: addr.unit,
              city: addr.city || '',
              state: addr.state || '',
              zipCode: addr.postcode || '',
              location: { lat: addr.lat, lng: addr.lng },
              selected: true,
              streetName: extractStreetName(fullStreet),
            };
          });

          setCreationAddresses(workloadAddresses);
          setCreationTerritory({
            type: 'radius' as any,
            center: selectedPoint,
          });
        } catch (walkErr: any) {
          // Fall through to closest-first as a fallback.
          console.warn('Walking pattern failed, falling back to closest-first:', walkErr);
          setProgressMessage(`${walkErr.message || 'Walking pattern failed'} — falling back to closest first…`);
          const result = await addressDatabaseService.getClosestAddresses(
            selectedPoint,
            targetCount,
            (msg) => setProgressMessage(msg)
          );
          const addresses = result.addresses;
          foundAddresses = addresses.length;
          const workloadAddresses = addresses.map((addr: AddressData) => {
            const fullStreet = `${addr.houseNumber} ${addr.street}`.trim();
            return {
              street: fullStreet,
              unit: addr.unit,
              city: addr.city || '',
              state: addr.state || '',
              zipCode: addr.postcode || '',
              location: { lat: addr.lat, lng: addr.lng },
              selected: true,
              streetName: extractStreetName(fullStreet),
            };
          });
          setCreationAddresses(workloadAddresses);
          setCreationTerritory({
            type: 'radius' as any,
            center: selectedPoint,
          });
        }
      } else if (mode === 'communities') {
        // Map all proximal communities - larger radius, cluster into workloads
        setProgressMessage('Discovering all proximal communities...');
        const result = await addressDatabaseService.discoverProximalCommunities(
          selectedPoint,
          500, // Target 500 per workload
          (msg) => setProgressMessage(msg),
          maxTotalHomes // Pass max total limit
        );

        const allAddresses = result.addresses;
        const communities = result.communities;
        foundAddresses = allAddresses.length;

        // Set community workloads for display
        setCommunityWorkloads(communities.map(c => ({ name: c.name, count: c.addressCount })));

        // Convert to workload addresses with community/polygon ID
        const workloadAddresses = allAddresses.map((addr: AddressData & { communityId?: string; communityName?: string }) => {
          const fullStreet = `${addr.houseNumber} ${addr.street}`.trim();
          return {
            street: fullStreet,
            unit: addr.unit,
            city: addr.city || '',
            state: addr.state || '',
            zipCode: addr.postcode || '',
            location: { lat: addr.lat, lng: addr.lng },
            selected: true,
            polygonId: addr.communityId || 'default',
            streetName: fullStreet.replace(/^\d+\s*/, '').trim(),
          };
        });

        // Add each community as a "polygon" for the splitting logic
        for (const community of communities) {
          addCreationPolygon({
            id: community.id,
            paths: [], // No actual polygon, just a logical grouping
            bounds: community.bounds,
            addressCount: community.addressCount,
          });
        }

        setCreationAddresses(workloadAddresses);
        setCreationTerritory({
          type: 'radius' as any,
          center: selectedPoint,
        });

      } else {
        // Simple point-based targeting - find closest N addresses
        setProgressMessage(`Finding ${targetCount} closest homes...`);
        const result = await addressDatabaseService.getClosestAddresses(
          selectedPoint,
          targetCount,
          (msg) => setProgressMessage(msg)
        );

        const addresses = result.addresses;
        foundAddresses = addresses.length;

        // Count apartments
        const addressesWithUnits = addresses.filter(addr => addr.unit);
        if (addressesWithUnits.length > 0) {
          setApartmentStats({
            buildings: new Set(addressesWithUnits.map(a => `${a.houseNumber} ${a.street}`)).size,
            units: addressesWithUnits.length,
          });
        }

        // Convert to workload addresses
        const workloadAddresses = addresses.map((addr: AddressData) => {
          const fullStreet = `${addr.houseNumber} ${addr.street}`.trim();
          return {
            street: fullStreet,
            unit: addr.unit,
            city: addr.city || '',
            state: addr.state || '',
            zipCode: addr.postcode || '',
            location: { lat: addr.lat, lng: addr.lng },
            selected: true,
            streetName: fullStreet.replace(/^\d+\s*/, '').trim(),
          };
        });

        setCreationAddresses(workloadAddresses);
        setCreationTerritory({
          type: 'radius' as any,
          center: selectedPoint,
        });
      }

      // Use local variable to check, not state (fixes race condition)
      if (foundAddresses === 0) {
        setError('No addresses found near this location. Try a different area or use polygon drawing.');
      }
    } catch (err: any) {
      console.error('Error finding addresses:', err);
      setError(err.message || 'Failed to find addresses near this location');
    } finally {
      setIsLoading(false);
      setProgressMessage(null);
    }
  }, [selectedPoint, setCreationTerritory, setCreationAddresses, addCreationPolygon, clearCreationPolygons]);

  // Handle "Done Drawing" - fetch addresses for all polygons
  const handleDoneDrawing = useCallback(async () => {
    if (polygons.length === 0) {
      setError('Draw at least one area (3+ points, then "Finish area") to define your canvassing area.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setProgressMessage(null);
    setApartmentStats(null);
    clearCreationPolygons();

    try {
      const allAddresses: Array<{
        street: string;
        unit?: string;
        city: string;
        state: string;
        zipCode: string;
        location: { lat: number; lng: number };
        selected: boolean;
        polygonId: string;
        streetName: string;
      }> = [];

      let totalBuildings = 0;
      let totalUnits = 0;

      // Process each polygon
      for (let i = 0; i < polygons.length; i++) {
        const polygonData = polygons[i];
        setProgressMessage(`Processing area ${i + 1} of ${polygons.length}...`);

        // Add polygon to store
        const polygonTerritory: PolygonTerritory = {
          id: polygonData.id,
          paths: polygonData.paths,
          bounds: polygonData.bounds,
        };
        addCreationPolygon(polygonTerritory);

        // Get addresses for this polygon
        const result = await addressDatabaseService.getAddressesInPolygonRefined(
          polygonData.paths,
          (msg) => setProgressMessage(`Area ${i + 1}: ${msg}`)
        );

        const addresses = result.addresses;

        // Count apartments
        const addressesWithUnits = addresses.filter(addr => addr.unit);
        if (addressesWithUnits.length > 0) {
          totalBuildings += new Set(addressesWithUnits.map(a => `${a.houseNumber} ${a.street}`)).size;
          totalUnits += addressesWithUnits.length;
        }

        // Convert to workload addresses with polygon ID
        const workloadAddresses = addresses.map((addr: AddressData) => {
          const fullStreet = `${addr.houseNumber} ${addr.street}`.trim();
          return {
            street: fullStreet,
            unit: addr.unit,
            city: addr.city || '',
            state: addr.state || '',
            zipCode: addr.postcode || '',
            location: { lat: addr.lat, lng: addr.lng },
            selected: true,
            polygonId: polygonData.id,
            streetName: extractStreetName(fullStreet),
          };
        });

        allAddresses.push(...workloadAddresses);
      }

      // Update apartment stats
      if (totalUnits > 0) {
        setApartmentStats({ buildings: totalBuildings, units: totalUnits });
      }

      // Store all addresses
      setCreationAddresses(allAddresses);

      // Set territory to indicate multi-polygon mode
      setCreationTerritory({
        type: 'polygon',
        polygon: polygons[0].paths, // First polygon for backwards compatibility
        center: null,
      });

      if (allAddresses.length === 0) {
        setError('No addresses found in the selected areas. Try drawing larger areas or use CSV upload.');
      }

      // Turn off drawing mode when done
      setDrawingMode(null);
    } catch (err: any) {
      console.error('Error getting addresses:', err);
      setError(err.message || 'Failed to get addresses in the selected areas');
    } finally {
      setIsLoading(false);
      setProgressMessage(null);
    }
  }, [polygons, setCreationTerritory, setCreationAddresses, addCreationPolygon, clearCreationPolygons]);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Select Your Canvassing Areas
        </h2>
        <p className="text-gray-600">
          Search for a location, then draw one or more polygons to define your canvassing territories.
          Each polygon can become a separate workload, or they can be combined and split smartly by street.
        </p>
      </div>

      {/* School Address Indicator */}
      {selectedProject?.schoolAddress && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start gap-3">
          <MapPin className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-indigo-900">
              Map centered on: {selectedProject.schoolAddress.address}
            </p>
            <p className="text-xs text-indigo-700">
              {selectedProject.schoolAddress.city}, {selectedProject.schoolAddress.state} {selectedProject.schoolAddress.zipCode}
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <GoogleMapProvider>
          <PlacesAutocomplete
            onPlaceSelect={handlePlaceSelect}
            placeholder="Search for an address or location..."
            className="max-w-md"
          />
        </GoogleMapProvider>
      </div>

      {/* Map */}
      <div className="relative">
        <GoogleMapProvider>
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <BaseMap
              center={mapCenter}
              zoom={15}
              height={500}
              onClick={handleMapClick}
            >
              {/* In-progress polygon drawing */}
              {drawingMode === 'polygon' && vertices.length > 0 && (
                <>
                  <Polyline path={vertices} options={{ strokeColor: '#059669', strokeWeight: 2 }} />
                  {vertices.map((v, i) => (
                    <Marker
                      key={`v-${i}`}
                      position={v}
                      icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 4, fillColor: '#059669', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1 } as any}
                    />
                  ))}
                </>
              )}
              {/* Completed areas */}
              {polygons.map((p, i) => (
                <Polygon
                  key={p.id}
                  paths={p.paths}
                  options={{ fillColor: colorAt(i), fillOpacity: 0.2, strokeColor: colorAt(i), strokeWeight: 2, clickable: false }}
                />
              ))}
              {/* Draggable search marker (point mode) */}
              {searchMarker && (
                <Marker
                  position={searchMarker}
                  draggable
                  onDragEnd={handleMarkerDragEnd}
                  title="Drag to reposition center point"
                />
              )}
            </BaseMap>
          </div>

          {/* Drawing controls */}
          <div className="absolute bottom-4 left-4 z-10 bg-white rounded-lg shadow-md p-2 flex items-center gap-2">
            <button
              onClick={() => { setDrawingMode(drawingMode === 'marker' ? null : 'marker'); setVertices([]); }}
              className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium ${drawingMode === 'marker' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              title="Point targeting"
            >
              <Target className="h-4 w-4" /> Point
            </button>
            <button
              onClick={() => { setDrawingMode(drawingMode === 'polygon' ? null : 'polygon'); setVertices([]); }}
              className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium ${drawingMode === 'polygon' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              title="Draw areas"
            >
              <Pentagon className="h-4 w-4" /> Draw areas
            </button>

            {drawingMode === 'polygon' && (
              <>
                <span className="text-xs text-gray-600 px-1">{vertices.length} pts</span>
                <button onClick={finishArea} disabled={vertices.length < 3} className="px-2 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium disabled:opacity-50">Finish area</button>
                <button onClick={() => setVertices((v) => v.slice(0, -1))} disabled={!vertices.length} className="px-2 py-1.5 bg-gray-100 text-gray-700 rounded text-xs disabled:opacity-50">Undo</button>
              </>
            )}

            {polygons.length > 0 && (
              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                {polygons.length} area{polygons.length !== 1 ? 's' : ''}
              </span>
            )}
            {polygons.length > 0 && (
              <button onClick={handleDoneDrawing} className="px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 text-xs font-medium">
                Done
              </button>
            )}

            <div className="w-px h-6 bg-gray-300 mx-1" />
            <button
              onClick={() => {
                setCreationTerritory({ type: null, polygon: null, center: null });
                setCreationAddresses([]);
                clearCreationPolygons();
                setSearchMarker(null);
                setError(null);
                setProgressMessage(null);
                setApartmentStats(null);
                setVertices([]);
                setPolygons([]);
                setDrawingMode(null);
              }}
              className="px-2 py-1.5 bg-red-50 text-red-600 rounded text-xs hover:bg-red-100"
            >
              Clear
            </button>
          </div>

          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-white/75 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
                <p className="mt-2 text-gray-600">Fetching addresses from database...</p>
                {progressMessage && (
                  <p className="mt-1 text-sm text-indigo-600 font-medium">
                    {progressMessage}
                  </p>
                )}
              </div>
            </div>
          )}
        </GoogleMapProvider>
      </div>

      {/* Error - only show if no addresses found */}
      {error && creation.addresses.length === 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Info */}
      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Data source:</strong> OpenStreetMap + Google Geocoding
        </p>
        <p className="text-xs text-blue-600 mt-1">
          Addresses are discovered from OpenStreetMap, then coordinates are refined using Google Geocoding for accurate rooftop placement.
        </p>
      </div>

      {/* Results summary */}
      {creation.addresses.length > 0 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start justify-end mb-2">
            <button
              type="button"
              onClick={() => exportCreationAddressesToCSV(creation.addresses, 'saturation')}
              className="flex items-center gap-1 px-3 py-1 text-xs bg-white border border-green-300 rounded text-green-800 hover:bg-green-100"
            >
              <Download className="w-3 h-3" />
              Download CSV
            </button>
          </div>
          <div className="flex items-start">
            <Check className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="text-green-800 font-medium">
                Found {creation.addresses.length} addresses
                {creation.polygons?.length > 0 && ` in ${creation.polygons.length} area${creation.polygons.length !== 1 ? 's' : ''}`}
              </p>
              {apartmentStats && (
                <p className="text-green-700 text-sm mt-1">
                  Including {apartmentStats.units} apartment units from {apartmentStats.buildings} building{apartmentStats.buildings !== 1 ? 's' : ''}
                </p>
              )}
              {communityWorkloads.length > 0 && (
                <div className="mt-2">
                  <p className="text-green-700 text-sm font-medium">
                    Discovered {communityWorkloads.length} communities:
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {communityWorkloads.slice(0, 6).map((cw, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        {cw.name}: {cw.count}
                      </span>
                    ))}
                    {communityWorkloads.length > 6 && (
                      <span className="text-xs text-green-600">+{communityWorkloads.length - 6} more</span>
                    )}
                  </div>
                </div>
              )}
              {creation.polygons?.length > 1 && communityWorkloads.length === 0 && (
                <p className="text-green-700 text-sm mt-1">
                  {creation.polygons.length} separate territories drawn - each can become its own workload
                </p>
              )}
              <p className="text-green-600 text-sm mt-1">
                Click Next to configure how these addresses will be split into workloads.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Point Target Modal */}
      <PointTargetModal
        isOpen={showPointModal}
        onClose={() => setShowPointModal(false)}
        onSubmit={handlePointTargetSubmit}
        position={selectedPoint}
      />

      {/* Instructions */}
      <div className="mt-6 space-y-4">
        <h3 className="font-medium text-gray-900">Two ways to select addresses:</h3>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Point-based method */}
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
            <div className="flex items-center mb-2">
              <Target className="h-5 w-5 text-indigo-600 mr-2" />
              <h4 className="font-medium text-indigo-900">Point Targeting</h4>
            </div>
            <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside">
              <li>Click "Point" tool</li>
              <li>Click on the map to place your center point</li>
              <li>Enter how many homes you want to target</li>
              <li>Optionally enable "Map All Communities" to auto-discover nearby neighborhoods</li>
            </ol>
            <p className="text-xs text-indigo-600 mt-2">
              Best for: Quick targeting around a specific location
            </p>
          </div>

          {/* Polygon method */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center mb-2">
              <Pentagon className="h-5 w-5 text-gray-600 mr-2" />
              <h4 className="font-medium text-gray-900">Polygon Drawing</h4>
            </div>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Click "Draw areas" tool</li>
              <li>Click points on map to outline an area</li>
              <li>Click "Finish area" (3+ points); draw more if needed</li>
              <li>Click "Done" to fetch all addresses</li>
            </ol>
            <p className="text-xs text-gray-500 mt-2">
              Best for: Precise boundary control over specific areas
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaturationPointSelector;
